// メールテンプレート編集
import React, { useState, useEffect } from 'react';
import { render } from 'react-dom';
import axios from '../../../axios';
import { handleBrowserBack } from '../../../BrowserBack';
import { http_status_code_list, maintenance_type } from '../../../Const';
import Maintenance from '../../../molecules/Maintenance';
import LoadingModal from '../../../molecules/LoadingModal';
import ReactDatePicker from 'react-datepicker';
import ja from 'date-fns/locale/ja';
import PopupStaff from '../../../molecules/PopupStaff';
import ValidMessage from '../../../atoms/ValidMessage';
import StaffNavbar from '../../../molecules/StaffNavbar';

interface Template {
  id?: string;
  ja_name?: string;
  subject?: string;
  body?: string;
  type_id?: string;
  type?: string;
  start_date?: string;
  end_date?: string;
}

interface ErrorText {
  subject?: string;
  body?: string;
  start_date?: string;
}

const MailTemplateEdit = () => {
  const [template, setTemplate] = useState<Template>();
  const [error_text, setErrorText] = useState<ErrorText>();
  const [is_confirm, setIsConfirm] = useState<boolean>(false);
  const [is_popup, setIsPopup] = useState<boolean>(false);
  // ローディング画面表示状態
  const [is_loading, setIsLoading] = useState(true);
  const url_params = new URLSearchParams(document.location.search.substring(1));
  const mail_id = url_params.get('mail_id');

  // グローバルナビゲーション表示用
  const [role, setRole] = useState('');
  const [has_loan_aproval_system, setHasLoanAprovalSystem] = useState(false);
  // 組織区分コードの取得
  const node = document.getElementById('mail-template-edit');

  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowserBack();
    // 表示するメールテンプレートの取得
    getMailTempalte();
    // グローバルナビゲーション表示用
    setUserRole();
  }, []);

  // タイトル入力
  const handleChangeSubject = (value: string) => {
    setTemplate({ ...template, subject: value });
  };

  // 本文入力
  const handleChangeBody = (value: string) => {
    setTemplate({ ...template, body: value });
  };

  // 開始日入力
  const handleChangeStartDate = (value: Date) => {
    const year = value.getFullYear();
    const month = value.getMonth() + 1;
    const day = value.getDate();
    const start_date =
      year + '-' + month.toString().padStart(2, '0') + '-' + day.toString().padStart(2, '0');
    setTemplate({ ...template, start_date: start_date });
  };

  // メールテンプレートを取得し表示する
  const getMailTempalte = async () => {
    try {
      const response = await axios.get(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/get_mail_template`,
        {
          params: {
            mail_id: mail_id,
          },
        }
      );
      if (response.data.result) {
        setTemplate(response.data.template);
      } else {
        console.error(response.data.message);
        alert(response.data.message);
      }
    } catch (error) {
      console.error(error);
      if (error.response.status === http_status_code_list.unauthorized) {
        // セッション切れの場合はログイン画面に遷移
        location.replace(error.response.data.url);
        return;
      }
      alert(error.response.data.message);
    }
    setIsLoading(false);
  };

  // 削除ボタン押下時
  const checkDeleteMailTemplate = async () => {
    try {
      setIsLoading(true);
      setErrorText({});
      const response = await axios.post(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/check_delete_mail_template`,
        {
          mail_id: mail_id,
        }
      );
      if (response.data.result) {
        setIsPopup(true);
      } else {
        console.error(response.data.message);
        setErrorText({
          start_date: response.data.message,
        });
      }
    } catch (error) {
      console.error(error);
      if (error.response.status === http_status_code_list.unauthorized) {
        // セッション切れの場合はログイン画面に遷移
        location.replace(error.response.data.url);
        return;
      }
      alert(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 削除確認ポップアップのOKボタン押下時
  const deleteMailTemplate = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/delete_mail_template`,
        {
          mail_id: mail_id,
        }
      );

      if (response.data.result) {
        // 編集完了画面に遷移
        location.replace(
          `${location.origin}${process.env.MIX_MOCK_URL}/maintenance/mail_template_complete`
        );
      } else {
        console.error(response.data.message);
        alert(response.data.message);
        setIsLoading(false);
        setIsPopup(false);
      }
    } catch (error) {
      console.error(error);
      if (error.response.status === http_status_code_list.unauthorized) {
        // セッション切れの場合はログイン画面に遷移
        location.replace(error.response.data.url);
        return;
      }
      alert(error.response.data.message);
      setIsLoading(false);
    }
  };

  // 登録ボタン押下時
  const handleRegister = async () => {
    setIsLoading(true);
    setErrorText({});
    try {
      const response = await axios.post(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/check_mail_template`,
        {
          id: mail_id,
          type_id: template?.type_id,
          subject: template?.subject,
          body: template?.body,
          start_date: template?.start_date,
          end_date: template?.end_date,
        }
      );

      if (response.data.result) {
        setIsConfirm(true);
      } else {
        setErrorText({
          subject: response.data.message.subject,
          body: response.data.message.body,
          start_date: response.data.message.start_date,
        });
      }
    } catch (error) {
      console.error(error);
      if (error.response.status === http_status_code_list.unauthorized) {
        // セッション切れの場合はログイン画面に遷移
        location.replace(error.response.data.url);
        return;
      }
      alert(error.response.data.message);
    }
    setIsLoading(false);
  };

  // 確認ボタン押下時
  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/register_mail_template`,
        {
          id: mail_id,
          type_id: template?.type_id,
          subject: template?.subject,
          body: template?.body,
          start_date: template?.start_date,
          end_date: template?.end_date,
        }
      );

      if (response.data.result) {
        // 編集完了画面に遷移
        location.replace(
          `${location.origin}${process.env.MIX_MOCK_URL}/maintenance/mail_template_complete`
        );
      } else {
        setIsLoading(false);
        alert('システムエラーが発生しました');
      }
    } catch (error) {
      console.error(error);
      if (error.response.status === http_status_code_list.unauthorized) {
        // セッション切れの場合はログイン画面に遷移
        location.replace(error.response.data.url);
        return;
      }
      alert(error.response.data.message);
      setIsLoading(false);
    }
  };

  // ロールを設定する
  const setUserRole = () => {
    if (node === null) {
      // nodeが正しく取得できない場合は空文字とする
      setRole('');
      setHasLoanAprovalSystem(false);
      return;
    }

    // ロール名をセット
    setRole(node.dataset.role as string);

    // 融資稟議システム導入済みフラグをセット
    if ((node.dataset.has_loan_aproval_system as string) === '1') {
      setHasLoanAprovalSystem(true);
    } else {
      setHasLoanAprovalSystem(false);
    }
  };

  return (
    <>
      <StaffNavbar user_role={role} has_loan_aproval_system={has_loan_aproval_system} />
      <Maintenance active={maintenance_type.mail_templates}>
        <div className="page-title">
          メールテンプレート編集
          {is_confirm && '(確認)'}
        </div>
        {typeof template === 'object' ? (
          <>
            <table className="detail_table">
              <tbody>
                <tr>
                  <th>JA名</th>
                  <td>{template.ja_name}</td>
                </tr>
                <tr>
                  <th>種別</th>
                  <td>{template.type}</td>
                </tr>
                <tr>
                  <th>タイトル</th>
                  <td>
                    {is_confirm ? (
                      template.subject
                    ) : (
                      <>
                        <input
                          className={`form-control ${error_text?.subject && 'textarea-error'}`}
                          type="text"
                          value={template.subject}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            handleChangeSubject(event.target.value)
                          }
                        />
                        <ValidMessage message={error_text?.subject} />
                      </>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>本文</th>
                  <td>
                    {is_confirm ? (
                      template.body
                    ) : (
                      <>
                        <textarea
                          className={`form-control ${error_text?.body && 'textarea-error'}`}
                          rows={10}
                          cols={60}
                          value={template.body}
                          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                            handleChangeBody(event.target.value)
                          }
                        />
                        <ValidMessage message={error_text?.body} />
                      </>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>開始日</th>
                  <td>
                    {is_confirm && template.start_date ? (
                      template.start_date
                    ) : (
                      <>
                        <ReactDatePicker
                          className={`form-control ${error_text?.start_date && 'textarea-error'}`}
                          selected={new Date(template.start_date ?? '')}
                          onChange={handleChangeStartDate}
                          locale={ja}
                          dateFormat="yyyy-MM-dd"
                        />
                        <ValidMessage message={error_text?.start_date} />
                      </>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="footer-btn">
              {is_confirm ? (
                <>
                  <div
                    className="btn btn-mini btn-outline-success btn-text-color"
                    onClick={() => setIsConfirm(false)}
                  >
                    戻る
                  </div>
                  <div className="btn btn-mini btn-success" onClick={handleConfirm}>
                    確認
                  </div>
                </>
              ) : (
                <>
                  <a
                    className="btn btn-mini btn-outline-success btn-text-color"
                    href={`${location.origin}${process.env.MIX_MOCK_URL}/maintenance/mail_template_detail?mail_id=${mail_id}`}
                  >
                    キャンセル
                  </a>
                  <div className="btn btn-mini btn-danger" onClick={checkDeleteMailTemplate}>
                    削除
                  </div>
                  <div className="btn btn-mini btn-success" onClick={handleRegister}>
                    登録
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          <></>
        )}
        <LoadingModal show={is_loading} />
        <PopupStaff
          toggleShow={is_popup}
          text={'このメールテンプレートを削除します。よろしいですか？'}
          btn1_label={'OK'}
          btn1_css={'btn-danger'}
          btn1_func={deleteMailTemplate}
          btn2_label={'キャンセル'}
          btn2_css={'btn-outline-success btn-text-color'}
          btn2_func={() => {
            setIsPopup(false);
          }}
        />
      </Maintenance>
    </>
  );
};

if (document.getElementById('mail-template-edit')) {
  render(<MailTemplateEdit />, document.getElementById('mail-template-edit'));
}
